import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import FormHelperText from "@material-ui/core/FormHelperText";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";
import { IconButton, InputAdornment } from "@material-ui/core";
import { Colorize } from "@material-ui/icons";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
		width: "100%",
	},
	colorAdorment: {
		width: 20,
		height: 20,
	},
	selectContainer: {
		width: "100%",
		marginTop: 10,
	}
}));

const QueueSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	color: Yup.string().min(3, "Too Short!").max(9, "Too Long!").required(),
	greetingMessage: Yup.string(),
	transferToConnectionName: Yup.string().nullable(),
	messageLeavingConnection: Yup.string().nullable(),
	messageEnteringConnection: Yup.string().nullable(),
});

const QueueModal = ({ open, onClose, queueId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		color: "",
		greetingMessage: "",
		transferToConnectionName: null,
		messageLeavingConnection: "",
		messageEnteringConnection: "",
	};

	const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
	const [queue, setQueue] = useState(initialState);
	const [whatsapps, setWhatsapps] = useState([]);
	const [settings, setSettings] = useState([]);
	const greetingRef = useRef();

	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.get("/whatsapp");
				setWhatsapps(data);
			} catch (err) {
				toastError(err);
			}

			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		})();
	}, []);

	useEffect(() => {
		(async () => {
			if (!queueId) return;
			try {
				const { data } = await api.get(`/queue/${queueId}`);
				setQueue(prevState => {
					return { ...prevState, ...data };
				});
			} catch (err) {
				toastError(err);
			}
		})();

		return () => {
			setQueue(initialState);
		};
	}, [queueId, open]);

	const handleClose = () => {
		onClose();
		setQueue(initialState);
	};

	const handleSaveQueue = async values => {
		try {
			if (queueId) {
				await api.put(`/queue/${queueId}`, values);
			} else {
				await api.post("/queue", values);
			}
			toast.success("Cola guardada con éxito");
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	const getSettingValue = key => {
		const setting = settings.find(s => s.key === key);
		return setting ? setting.value : "";
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} scroll="paper">
				<DialogTitle>
					{queueId
						? `${i18n.t("queueModal.title.edit")}`
						: `${i18n.t("queueModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={queue}
					enableReinitialize={true}
					validationSchema={QueueSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveQueue(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<Field
									as={TextField}
									label={i18n.t("queueModal.form.name")}
									autoFocus
									name="name"
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
								<Field
									as={TextField}
									label={i18n.t("queueModal.form.color")}
									name="color"
									id="color"
									onFocus={() => {
										setColorPickerModalOpen(true);
										greetingRef.current.focus();
									}}
									error={touched.color && Boolean(errors.color)}
									helperText={touched.color && errors.color}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<div
													style={{ backgroundColor: values.color }}
													className={classes.colorAdorment}
												></div>
											</InputAdornment>
										),
										endAdornment: (
											<IconButton
												size="small"
												color="default"
												onClick={() => setColorPickerModalOpen(true)}
											>
												<Colorize />
											</IconButton>
										),
									}}
									variant="outlined"
									margin="dense"
								/>
								<ColorPicker
									open={colorPickerModalOpen}
									handleClose={() => setColorPickerModalOpen(false)}
									onChange={color => {
										values.color = color;
										setQueue(() => {
											return { ...values, color };
										});
									}}
								/>
								<div>
									<Field
										as={TextField}
										label={i18n.t("queueModal.form.greetingMessage")}
										type="greetingMessage"
										multiline
										inputRef={greetingRef}
										rows={5}
										fullWidth
										name="greetingMessage"
										error={
											touched.greetingMessage && Boolean(errors.greetingMessage)
										}
										helperText={
											touched.greetingMessage && errors.greetingMessage
										}
										variant="outlined"
										margin="dense"
									/>
								</div>

								{/* Campos de transferencia */}
								<div className={classes.selectContainer}>
									<FormControl 
										variant="outlined" 
										margin="dense" 
										className={classes.formControl}
										error={touched.transferToConnectionName && Boolean(errors.transferToConnectionName)}
									>
										<InputLabel id="transferToConnectionName-label">
											Conexión para Transferencia
										</InputLabel>
										<Select
											labelId="transferToConnectionName-label"
											id="transferToConnectionName"
											name="transferToConnectionName"
											value={values.transferToConnectionName || ""}
											onChange={(e) => {
												const value = e.target.value;
												if (value === "") {
													setFieldValue("transferToConnectionName", null);
												} else {
													setFieldValue("transferToConnectionName", value);
												}
											}}
											label="Conexión para Transferencia"
										>
											<MenuItem value="">
												<em>Desactivado</em>
											</MenuItem>
											{whatsapps.map((whatsapp) => (
												<MenuItem key={whatsapp.id} value={whatsapp.name}>
													{whatsapp.name}
												</MenuItem>
											))}
										</Select>
										{touched.transferToConnectionName && errors.transferToConnectionName && (
											<FormHelperText>{errors.transferToConnectionName}</FormHelperText>
										)}
									</FormControl>
								</div>

								{/* Campos condicionales que solo aparecen si hay una conexión seleccionada */}
								{values.transferToConnectionName && (
									<>
										<div>
											<Field
												as={TextField}
												label="Mensaje al Salir de la Conexión"
												type="text"
												multiline
												rows={3}
												fullWidth
												name="messageLeavingConnection"
												error={
													touched.messageLeavingConnection && 
													Boolean(errors.messageLeavingConnection)
												}
												helperText={
													touched.messageLeavingConnection 
														? errors.messageLeavingConnection 
														: "Este mensaje se envía cuando el usuario sale de la conexión actual"
												}
												variant="outlined"
												margin="dense"
											/>
										</div>
										<div>
											<Field
												as={TextField}
												label="Mensaje al Entrar en la Conexión"
												type="text"
												multiline
												rows={3}
												fullWidth
												name="messageEnteringConnection"
												error={
													touched.messageEnteringConnection && 
													Boolean(errors.messageEnteringConnection)
												}
												helperText={
													touched.messageEnteringConnection 
														? errors.messageEnteringConnection 
														: "Este mensaje se envía cuando el usuario entra en la nueva conexión"
												}
												variant="outlined"
												margin="dense"
											/>
										</div>
									</>
								)}
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("queueModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{queueId
										? `Actualizar`
										: `${i18n.t("queueModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default QueueModal;